import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  LabelList,
  Cell,
  ReferenceLine,
} from "recharts";
import { fetchR2CsvText } from "./utils/fetchR2Csv";
import { STATE_NAMES } from "./utils/helpers";
import "./Dashboard.css";

const AREA_CONFIG = [
  {
    key: "metro",
    title: "Metros",
    file: "best_performing_rank_metro_linked.csv",
    areaGetter: (row) => row.Metro || row.AreaName,
    parentGetter: (row) => row.State || row.StateName,
  },
  {
    key: "city",
    title: "Cities",
    file: "best_performing_rank_city_linked.csv",
    areaGetter: (row) => row.City || row.AreaName,
    parentGetter: (row) => row.State || row.StateName || row.Metro,
  },
  {
    key: "zipcode",
    title: "Zip Codes",
    file: "best_performing_rank_zipcode_linked.csv",
    areaGetter: (row) => row.Zipcode || row.AreaName,
    parentGetter: (row) => row.City || row.Metro || row.State,
  },
  {
    key: "neighborhood",
    title: "Neighborhoods",
    file: "best_performing_rank_neighborhood_linked.csv",
    areaGetter: (row) => row.Neighborhood || row.AreaName,
    parentGetter: (row) => row.City || row.Metro || row.State,
  },
];

const COLORS = {
  best: "#059669",
  worst: "#dc2626",
  text: "#0f172a",
  subtitleBest: "#047857", // slightly darker green for text
  subtitleWorst: "#b91c1c", // slightly darker red for text
};

const STATE_ABBR_BY_NAME = Object.fromEntries(
  Object.entries(STATE_NAMES).map(([abbr, full]) => [String(full).toLowerCase(), abbr])
);

const toStateAbbr = (value) => {
  if (!value) return "";
  const trimmed = String(value).trim();
  if (/^[A-Za-z]{2}$/.test(trimmed)) return trimmed.toUpperCase();
  const lookup = STATE_ABBR_BY_NAME[trimmed.toLowerCase()];
  if (lookup) return lookup;
  return trimmed.slice(0, 2).toUpperCase();
};

const stripStateFromName = (raw) => {
  if (!raw) return { name: "", stateAbbr: "" };
  let name = String(raw).trim();
  let stateAbbr = "";

  const commaMatch = name.match(/^(.*?)[,\s]+([A-Za-z]{2})$/);
  if (commaMatch) {
    name = commaMatch[1].trim();
    stateAbbr = commaMatch[2].trim().toUpperCase();
  }

  return { name, stateAbbr };
};

const formatDisplayLabel = (levelKey, row, rawAreaName) => {
  const { name: areaName, stateAbbr: areaStateFromName } = stripStateFromName(rawAreaName);
  const fallbackState = row.State || row.StateName || areaStateFromName;

  if (levelKey === "neighborhood" || levelKey === "zipcode") {
    const { name: cityName, stateAbbr: cityStateFromName } = stripStateFromName(row.City || "");
    const cityLabel = cityName || row.City || row.Metro || "";
    const stateAbbr = toStateAbbr(fallbackState || cityStateFromName || row.State);
    const suffix = [cityLabel, stateAbbr].filter(Boolean).join(", ");
    return suffix ? `${areaName}, ${suffix}` : areaName;
  }

  if (levelKey === "city" || levelKey === "metro") {
    const stateAbbr = toStateAbbr(fallbackState);
    return stateAbbr ? `${areaName} ${stateAbbr}` : areaName;
  }

  return areaName;
};

async function loadCsvRows(fileName, Papa) {
  const text = await fetchR2CsvText(`public/data/Best Performing Linked/${fileName}`);
  return new Promise((resolve, reject) => {
    Papa.parse(text, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: "greedy",
      transformHeader: (h) => String(h || "").trim(),
      complete: (result) => resolve(Array.isArray(result.data) ? result.data : []),
      error: reject,
    });
  });
}

const extractYoy = (row) => {
  const candidates = [
    row.Price_YoY_delta,
    row.PriceYoY,
    row.PriceYoYDelta,
    row.price_12m_delta,
  ];
  const firstValid = candidates.find((val) => Number.isFinite(Number(val)));
  const yoy = Number(firstValid);
  if (Number.isFinite(yoy) && Math.abs(yoy) < 80) {
    return yoy;
  }
  return null;
};

function buildChartData(rows, cfg) {
  const formatted = rows
    .map((row) => {
      const areaName = cfg.areaGetter(row);
      if (!areaName) return null;
      const rank = Number(row.Rank);
      if (!Number.isFinite(rank)) return null;
      const yoy = extractYoy(row);
      if (yoy === null) return null;
      const metric = yoy * 100; // convert to percentage

      const label = formatDisplayLabel(cfg.key, row, areaName);
      return {
        label,
        shortLabel: label,
        areaName: String(areaName),
        rank,
        score: Number.isFinite(Number(row.Score)) ? Number(row.Score) : null,
        date: row.Date,
        metric,
        valueLabel: `${metric >= 0 ? "" : ""}${metric.toFixed(2)}%`,
      };
    })
    .filter(Boolean);

  if (!formatted.length) {
    return {
      latestDate: null,
      data: [],
    };
  }

  const latestDate = formatted.reduce((acc, row) => {
    if (!row.date) return acc;
    if (!acc || row.date > acc) return row.date;
    return acc;
  }, "");

  const best = [...formatted]
    .sort((a, b) => b.metric - a.metric)
    .slice(0, 10)
    .map((row, idx) => ({
      ...row,
      bucket: "best",
      order: idx + 1,
      chartValue: row.metric,
    }));

  const worst = [...formatted]
    .sort((a, b) => a.metric - b.metric)
    .slice(0, 10)
    .map((row, idx) => ({
      ...row,
      bucket: "worst",
      order: idx + 1,
      chartValue: row.metric,
    }));

  const series = [...best, ...worst]
    .sort((a, b) => b.chartValue - a.chartValue)
    .map((row, idx) => ({
      ...row,
      id: `${row.bucket}-${idx}-${row.label}`,
    }));

  return {
    latestDate,
    series,
  };
}

function WinnersTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) {
    return null;
  }
  const datum = payload[0]?.payload;
  if (!datum) return null;
  return (
    <div className="winners-tooltip">
      <div className="winners-tooltip-title">{datum.label}</div>
      <div>Rank #{datum.rank}</div>
      {Number.isFinite(datum.score) && <div>Score: {datum.score.toFixed(2)}</div>}
      <div>Value: {datum.valueLabel}</div>
      {datum.date && <div>Updated {datum.date}</div>}
    </div>
  );
}

function SegmentChart({ data }) {
  if (!data || data.length === 0) {
    return <div className="winners-chart-placeholder">No ranking data available.</div>;
  }

  const chartHeight = Math.max(260, data.length * 32 + 40);

  return (
    <ResponsiveContainer width="100%" height={chartHeight}>
      <BarChart
        data={data}
        layout="vertical"
        margin={{ top: 12, right: 28, bottom: 12, left: 12 }}
      >
        <XAxis
          type="number"
          domain={["dataMin - 1", "dataMax + 1"]}
          hide
        />
        <YAxis
          dataKey="shortLabel"
          type="category"
          width={170}
          tick={{ fontSize: 12, fill: COLORS.text }}
          axisLine={false}
          tickLine={false}
        />
        <ReferenceLine x={0} stroke="#cbd5f5" strokeWidth={1} />
        <Tooltip content={<WinnersTooltip />} cursor={{ fill: "rgba(148, 163, 184, 0.15)" }} />
        <Bar
          dataKey="chartValue"
          radius={[0, 0, 0, 0]}
          barSize={18}
          background={{ fill: "rgba(226, 232, 240, 0.5)" }}
        >
          {data.map((entry) => (
            <Cell
              key={entry.id}
              fill={entry.chartValue >= 0 ? COLORS.best : COLORS.worst}
            />
          ))}
          <LabelList
            dataKey="valueLabel"
            content={({ x, width, y, value, payload }) => {
              if (value == null || y == null || !payload) return null;
              const chartValue = payload.chartValue;
              if (chartValue == null || x == null || width == null) return null;
              const isPositive = chartValue >= 0;
              const posX = isPositive ? x + width + 8 : x - 8;
              const anchor = isPositive ? "start" : "end";
              return (
                <text
                  x={posX}
                  y={y + 5}
                  fill={COLORS.text}
                  fontSize={12}
                  textAnchor={anchor}
                >
                  {value}
                </text>
              );
            }}
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

export default function WinnersPage({ onContinue, onBack }) {
  const [chartMap, setChartMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const Papa = (await import("papaparse")).default;
        const results = await Promise.all(
          AREA_CONFIG.map(async (cfg) => {
            const rows = await loadCsvRows(cfg.file, Papa);
            return [cfg.key, buildChartData(rows, cfg)];
          })
        );
        if (!cancelled) {
          setChartMap(Object.fromEntries(results));
          setError(null);
        }
      } catch (err) {
        console.error("[WinnersPage] Failed to load winners data:", err);
        if (!cancelled) {
          setError("Unable to load winners data right now.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const isReady = useMemo(() => {
    if (loading || error) return false;
    return AREA_CONFIG.every((cfg) => chartMap[cfg.key]);
  }, [loading, error, chartMap]);

  const globalLatestDate = useMemo(() => {
    if (!chartMap || Object.keys(chartMap).length === 0) return null;
    // Get date from metro if available, or any other
    const metroData = chartMap["metro"];
    if (metroData?.latestDate) return metroData.latestDate;
    const firstKey = Object.keys(chartMap)[0];
    return chartMap[firstKey]?.latestDate;
  }, [chartMap]);

  const titleDate = useMemo(() => {
    if (!globalLatestDate) return "";
    try {
      // date string is YYYY-MM
      const [yyyy, mm] = globalLatestDate.split("-");
      const date = new Date(parseInt(yyyy, 10), parseInt(mm, 10) - 1);
      return date.toLocaleString("en-US", { month: "long", year: "numeric" });
    } catch {
      return "";
    }
  }, [globalLatestDate]);

  return (
    <div className="dashboard">
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <h1>
          <strong>AlgoreitAI</strong>{" "}
          <span style={{ fontWeight: 400 }}>Market Analysis</span>
        </h1>
        <div style={{ fontSize: 12, color: "#444" }}>Raw Data Source: Zillow; Mls, US Census Statistics</div>
      </div>

      <div className="winners-subtitle-card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
        <div className="winners-subtitle">
          The Winners of the Month — <span style={{ color: COLORS.subtitleBest }}>The Best Performing Areas</span> Vs <span style={{ color: COLORS.subtitleWorst }}>The Worst</span> {globalLatestDate ? <span style={{ fontWeight: 400, color: "#64748b", fontSize: "0.9em" }}> — as of {globalLatestDate}</span> : ""}
        </div>
            <p>
              See the ten highest- and lowest-performing markets across Metros, Cities,
              Zip Codes, and Neighborhoods before diving into the full dashboard.
            </p>
          </div>
          <div className="winners-actions" style={{ marginTop: 0, alignSelf: "center" }}>
            <button className="btn" onClick={onBack}>
              ← Back
            </button>
            <button
              className="btn btn-primary"
              onClick={onContinue}
              disabled={!isReady && !error}
            >
              Continue →
            </button>
          </div>
        </div>
      </div>

      {loading && (
        <div className="winners-loading">Loading top performers...</div>
      )}
      {error && (
        <div className="winners-error">
          {error} Please try again later or use the Continue button to skip.
        </div>
      )}

      <div className="winners-grid">
        {AREA_CONFIG.map((cfg) => {
          const chartInfo = chartMap[cfg.key];
          return (
            <section key={cfg.key} className="winners-card">
              <header>
                <h3>{cfg.title}</h3>
                {chartInfo?.latestDate && (
                  <div className="winners-date">
                    Latest update {chartInfo.latestDate}
                  </div>
                )}
              </header>
              <div className="winners-card-body">
                {!isReady ? (
                  <div className="winners-chart-placeholder">Preparing chart...</div>
                ) : (
                  <SegmentChart data={chartInfo?.series || []} />
                )}
              </div>
            </section>
          );
        })}
      </div>

      <div className="winners-actions">
        <button className="btn" onClick={onBack}>
          ← Back
        </button>
        <button
          className="btn btn-primary"
          onClick={onContinue}
          disabled={!isReady && !error}
        >
          Continue →
        </button>
      </div>
    </div>
  );
}

